import { useEffect, useState } from "react";
import { ChatCircleDots, Trash } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { fetchComments, postComment, replyToComment, deleteComment } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BLOG } from "@/constants/testIds";

const CommentSection = ({ slug }) => {
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [text, setText] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [replyingId, setReplyingId] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [replySubmitting, setReplySubmitting] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetchComments(slug)
      .then(setComments)
      .finally(() => setLoading(false));
  }, [slug]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.includes("@") || text.trim().length < 2) {
      setError("Fill in your name, a valid email, and a comment.");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      const created = await postComment(slug, { name: name.trim(), email: email.trim(), comment_text: text.trim() });
      setComments((prev) => [...prev, created]);
      setName("");
      setEmail("");
      setText("");
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not post comment. Try again shortly.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleReplySubmit = async (commentId) => {
    if (!replyText.trim()) return;
    setReplySubmitting(true);
    try {
      const updated = await replyToComment(slug, commentId, replyText.trim());
      setComments((prev) => prev.map((c) => (c.id === commentId ? updated : c)));
      setReplyingId(null);
      setReplyText("");
    } catch {
      // best-effort, keep reply box open on failure
    } finally {
      setReplySubmitting(false);
    }
  };

  const handleDelete = async (commentId) => {
    try {
      await deleteComment(slug, commentId);
      setComments((prev) => prev.filter((c) => c.id !== commentId));
    } catch {
      // best-effort
    }
  };

  return (
    <div data-testid={BLOG.commentsSection} className="mt-16 pt-10 border-t border-white/10">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#7C93B8] mb-6 flex items-center gap-2">
        <ChatCircleDots size={16} /> Comments ({comments.length})
      </p>

      {loading ? (
        <p className="text-sm text-white/40 mb-8">Loading comments...</p>
      ) : comments.length === 0 ? (
        <p data-testid={BLOG.commentsEmpty} className="text-sm text-white/40 mb-8">Be the first to comment.</p>
      ) : (
        <div className="space-y-4 mb-10">
          {comments.map((c) => (
            <div key={c.id} data-testid={BLOG.commentItem(c.id)} className="glass-panel rounded-xl p-4">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-sm font-bold text-white">{c.name}</span>
                <span className="text-xs text-white/30">{new Date(c.created_at).toLocaleDateString()}</span>
              </div>
              <p className="text-sm text-white/60 leading-relaxed">{c.comment_text}</p>

              {c.reply_text && (
                <div data-testid={BLOG.commentReplyBadge(c.id)} className="mt-3 ml-4 pl-3 border-l-2 border-white/30">
                  <span className="text-xs font-bold uppercase tracking-wider text-white">Skilmark Team</span>
                  <p className="text-sm text-white/60 leading-relaxed mt-1">{c.reply_text}</p>
                </div>
              )}

              {isAdmin && (
                <div className="flex items-center gap-3 mt-3">
                  <button
                    data-testid={BLOG.commentReplyBtn(c.id)}
                    onClick={() => {
                      setReplyingId(replyingId === c.id ? null : c.id);
                      setReplyText(c.reply_text || "");
                    }}
                    className="text-xs text-white/40 hover:text-white transition-colors duration-300"
                  >
                    {c.reply_text ? "Edit Reply" : "Reply"}
                  </button>
                  <button
                    data-testid={BLOG.commentDeleteBtn(c.id)}
                    onClick={() => handleDelete(c.id)}
                    className="text-xs text-white/40 hover:text-red-400 transition-colors duration-300 flex items-center gap-1"
                  >
                    <Trash size={12} /> Delete
                  </button>
                </div>
              )}

              {isAdmin && replyingId === c.id && (
                <div className="mt-3 flex gap-2">
                  <Textarea
                    data-testid={BLOG.commentReplyInput(c.id)}
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Reply as Skilmark Team..."
                    rows={2}
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/30 text-sm"
                  />
                  <button
                    data-testid={BLOG.commentReplySubmitBtn(c.id)}
                    onClick={() => handleReplySubmit(c.id)}
                    disabled={replySubmitting}
                    className="rounded-full bg-[#2563EB] text-white text-xs font-semibold px-4 py-2 h-fit disabled:opacity-50"
                  >
                    Send
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate className="glass-panel rounded-2xl p-6 space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Input
            data-testid={BLOG.commentNameInput}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name"
            className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
          />
          <Input
            data-testid={BLOG.commentEmailInput}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@yourapp.com"
            className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
          />
        </div>
        <Textarea
          data-testid={BLOG.commentTextInput}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Share your thoughts..."
          rows={3}
          className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
        />
        <button
          data-testid={BLOG.commentSubmitBtn}
          type="submit"
          disabled={submitting}
          className="rounded-full bg-[#2563EB] text-white font-semibold px-5 py-2.5 text-sm hover:bg-[#3B82F6] transition-colors duration-300 disabled:opacity-50"
        >
          {submitting ? "Posting..." : "Post Comment"}
        </button>
        {error && <p data-testid={BLOG.commentError} className="text-sm text-red-400">{error}</p>}
      </form>
    </div>
  );
};

export default CommentSection;
